/**
 * CessationInpiController — wizard cessation INPI §25.C.
 *
 * Orchestre la séquence côté utilisateur :
 *   1. Choix d'archivage gratuit A (en ligne 10 ans) ou B (export ZIP 90 j).
 *   2. Submission `initiateInpiCessation` → reçoit `{inpiFormality:
 *      {id, deepLink}}`. Affiche le bouton de signature INPI et démarre
 *      un polling long de l'état lifecycle (toutes les 30 s, max 20 fois)
 *      pour détecter la bascule à acceptation.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const POLL_INPI_MAX = 20;
const POLL_INPI_INTERVAL_MS = 30000;

export default class CessationInpiController extends BaseController {
  static targets = [
    "archiveOption",
    "submitButton",
    "errorBlock",
    "errorMessage",
    "resultBlock",
    "deepLink",
    "pollingStatus",
  ];

  connect() {
    this._submitting = false;
    this._inpiPollTimer = null;
    this._refreshSubmitState();
  }

  disconnect() {
    if (this._inpiPollTimer !== null) {
      clearTimeout(this._inpiPollTimer);
      this._inpiPollTimer = null;
    }
  }

  archiveOptionChanged() {
    this._clearError();
    this._refreshSubmitState();
  }

  async submitDeposit(event) {
    if (event) event.preventDefault();
    if (this._submitting) return;
    const archiveChoice = this._selectedOption();
    if (archiveChoice === null) return;

    this._submitting = true;
    this._clearError();
    this._setSubmitButtonDisabled(true);
    this._setSubmitButtonLabel("Dépôt en cours…");

    try {
      const response = await api.initiateInpiCessation({ archiveChoice });
      const deepLink = response?.inpiFormality?.deepLink;
      if (typeof deepLink !== "string" || deepLink === "") {
        throw new Error("Réponse INPI invalide (deepLink manquant).");
      }
      this._showResult(deepLink);
      this._startInpiPolling();
    } catch (err) {
      this._showError(this._extractErrorMessage(err, "Le dépôt INPI a échoué."));
      this._setSubmitButtonLabel("Préparer ma formalité INPI de cessation");
      this._setSubmitButtonDisabled(false);
    } finally {
      this._submitting = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers privés
  // ---------------------------------------------------------------------------

  _selectedOption() {
    if (!this.hasArchiveOptionTarget) return null;
    const selected = this.archiveOptionTargets.find((r) => r.checked);
    return selected ? selected.value : null;
  }

  _refreshSubmitState() {
    if (!this.hasSubmitButtonTarget) return;
    this._setSubmitButtonDisabled(this._selectedOption() === null);
  }

  _startInpiPolling() {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const lifecycle = await api.getLifecycle();
        if (lifecycle && lifecycle.lifecycleStatus === null) {
          this._setPollingStatus("Le dépôt INPI a été rejeté. Consultez votre email.");
          return;
        }
        if (
          lifecycle &&
          (lifecycle.lifecycleStatus === "archived_readonly" ||
            lifecycle.lifecycleStatus === "closed_no_archive")
        ) {
          this._setPollingStatus(
            "Cessation acceptée par l'INPI. Votre compte est désormais en archivage.",
          );
          return;
        }
      } catch {
        // Ignore et retente.
      }
      if (attempts >= POLL_INPI_MAX) {
        this._setPollingStatus(
          "Suivi temporaire arrêté. Le statut INPI est mis à jour 2× par jour côté serveur.",
        );
        return;
      }
      this._inpiPollTimer = setTimeout(tick, POLL_INPI_INTERVAL_MS);
    };
    this._inpiPollTimer = setTimeout(tick, POLL_INPI_INTERVAL_MS);
  }

  _showResult(deepLink) {
    if (this.hasDeepLinkTarget) {
      this.deepLinkTarget.setAttribute("href", deepLink);
    }
    if (this.hasResultBlockTarget) {
      this.resultBlockTarget.hidden = false;
    }
    this._setSubmitButtonLabel("Formalité préparée");
    this._setSubmitButtonDisabled(true);
  }

  _showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
    }
    if (this.hasErrorBlockTarget) {
      this.errorBlockTarget.hidden = false;
    }
  }

  _clearError() {
    if (this.hasErrorBlockTarget) {
      this.errorBlockTarget.hidden = true;
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "";
    }
  }

  _setSubmitButtonDisabled(disabled) {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = disabled;
    this.submitButtonTarget.setAttribute("aria-disabled", disabled ? "true" : "false");
  }

  _setSubmitButtonLabel(label) {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.textContent = label;
  }

  _setPollingStatus(text) {
    if (this.hasPollingStatusTarget) {
      this.pollingStatusTarget.textContent = text;
    }
  }

  _extractErrorMessage(err, fallback) {
    if (err && typeof err === "object") {
      if (typeof err.message === "string" && err.message !== "") return err.message;
      const apiError = err.error || err.body?.error || null;
      if (apiError && typeof apiError.message === "string") return apiError.message;
    }
    return fallback;
  }
}
